import {
  cloneMap, DataModelBuilder, isSameData, isSameMap, mapToData, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import dayjs, { Dayjs } from 'dayjs';
import { TimeZone } from '../../../time/timezone.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { EventProfile } from '../event-profile.js';
import { SerialNumberScope } from './serial-number-scope.js';
import { LegacySerialNumberStrategy } from './legacy-serial-number-strategy.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { LegacyStandardSerialNumberPool } from './legacy-standard-serial-number-pool.js';
import { EventTicket } from '../event-ticket.js';
import { isNotNullOrUndefined } from '../../../utility/null-safety.js';
import { TicketType } from '../ticket-type.js';
import { Price } from '../price.js';

/**
 * @deprecated Use {@link PriceBasedSerialNumberPool} instead.
 */
export class LegacyPriceBasedSerialNumberPool extends LegacyStandardSerialNumberPool {
  public static readonly TYPE_ID: string = '4e53ae190d9a4e428b9a1a1c46617114';

  private constructor(
      businessRef: BusinessModuleRef,
      eventId: string | null,
      creationDate: Dayjs,
      shortCode: string,
      scope: SerialNumberScope,
      totalTicketGroups: number,
      ticketGroups: Map<string, number>,
      serialNumbers: Map<string, number>,
  ) {
    super(
        businessRef,
        eventId,
        creationDate,
        shortCode,
        scope,
        totalTicketGroups,
        ticketGroups,
        serialNumbers,
    );
  }

  get strategy(): LegacySerialNumberStrategy {
    return LegacySerialNumberStrategy.AssignByTicketPrice;
  }

  get $typeId(): string {
    return LegacyPriceBasedSerialNumberPool.TYPE_ID;
  }

  /**
   * @deprecated Use only for migration purposes.
   *
   * @return {Map<string, number>} The serial numbers.
   */
  getSerialNumbers(): Map<string, number> {
    return this.serialNumbers;
  }

  static forEvent(event: EventProfile): LegacyPriceBasedSerialNumberPool | null {
    if(event.shortCode == null) {
      return null;
    }

    return new LegacyPriceBasedSerialNumberPool(
        event.businessRef,
        event.uniqueId,
        dayjs(),
        event.shortCode,
        SerialNumberScope.Event,
        0,
        new Map<string, number>(),
        new Map<string, number>(),
    );
  }

  static forBusiness(businessRef: BusinessModuleRef, shortCode: string): LegacyPriceBasedSerialNumberPool {
    return new LegacyPriceBasedSerialNumberPool(
        businessRef,
        null,
        dayjs(),
        shortCode,
        SerialNumberScope.Business,
        0,
        new Map<string, number>(),
        new Map<string, number>(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyPriceBasedSerialNumberPool {
    return new LegacyPriceBasedSerialNumberPool(
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.getOrNull<string>(map, 'eventId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<string>(map, 'shortCode'),
        toEnumFromString(MapUtils.get<string>(map, 'scope'), SerialNumberScope),
        MapUtils.get<number>(map, 'totalTicketGroups'),
        MapUtils.getMap(map, 'ticketGroups', (k, v) => ({ key: k, value: Number(v) })),
        MapUtils.getMap(map, 'serialNumbers', (k, v) => ({ key: k, value: Number(v) })),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'eventId': this.eventId,
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'shortCode': this.shortCode,
      'scope': fromEnumAsString(this.scope),
      'totalTicketGroups': this.totalTicketGroups,
      'ticketGroups': mapToData(this.ticketGroups, (k, v) => ({ key: k, value: v })),
      'serialNumbers': mapToData(this.serialNumbers, (k, v) => ({ key: k, value: v })),
    };
  }

  clone(): this {
    return new LegacyPriceBasedSerialNumberPool(
        this.businessRef.clone(),
        this.eventId,
        TimeZone.cloneDate(this.creationDate),
        this.shortCode,
        this.scope,
        this.totalTicketGroups,
        cloneMap(this.ticketGroups, (v) => v),
        cloneMap(this.serialNumbers, (v) => v),
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return this.eventId === other.eventId &&
        isSameData(this.businessRef, other.businessRef) &&
        this.creationDate == other.creationDate &&
        this.shortCode === other.shortCode &&
        this.scope === other.scope &&
        this.totalTicketGroups === other.totalTicketGroups &&
        isSameMap(other.ticketGroups, this.ticketGroups, (lh, rh) => lh === rh) &&
        isSameMap(other.serialNumbers, this.serialNumbers, (lh, rh) => lh === rh);
  }

  protected getTicketGroupId(ticket: EventTicket): string | null {
    if(isNotNullOrUndefined(ticket.discount)) {
      return null;
    }

    const price = this.getTicketPrice(ticket);
    return price.encode();
  }

  protected getTicketGroup(ticket: EventTicket): number {
    const price = this.getTicketPrice(ticket);
    return price.convertToCents;
  }

  private getTicketPrice(ticket: EventTicket): Price {
    return ticket.ticketType == TicketType.Protocol ?
          Price.zero(ticket.template.price.currency):
          ticket.template.price;
  }
}

export class LegacyPriceBasedSerialNumberPoolBuilder extends DataModelBuilder<LegacyPriceBasedSerialNumberPool> {
  build(context: SerializationContext, map: SerializedData): LegacyPriceBasedSerialNumberPool {
    return LegacyPriceBasedSerialNumberPool.fromMap(context, map);
  }
}
